/**
 * 아이템 아이콘 / 스킬 아이콘 / 데미지 숫자 폰트 / 타격 이펙트 도트 아트.
 */

import {
  blank,
  ellipse,
  fromStrings,
  line,
  outline,
  px,
  rect,
  roundRect,
  scaleGrid,
  stamp,
  type Grid,
  type Palette,
} from './grid';

export const ITEM_PALETTE: Palette = {
  o: '#2b1f1a',
  x: '#ffffff',
  X: '#cfd8e0',
  r: '#e8503c',
  R: '#a52d28',
  u: '#4a95e8',
  U: '#2560a8',
  c: '#f09030',
  C: '#b6631a',
  y: '#ffd24a',
  Y: '#c99510',
  n: '#7ed957',
  N: '#49a03c',
  m: '#f7e3bf',
  M: '#d4bb90',
  k: '#8a5a2a',
  K: '#5b3a1e',
  d: '#8f6fbf',
  D: '#5d4383',
  p: '#f4a0b4',
  P: '#d67f96',
  b: '#c4552f',
  B: '#8f6236',
};

function potion(liquid: string, liquidDark: string): Grid {
  const g = blank(16, 16);
  rect(g, 6, 0, 4, 2, 'k');
  rect(g, 6, 2, 4, 3, 'X');
  roundRect(g, 3, 4, 10, 11, 3, 'X');
  roundRect(g, 4, 6, 8, 8, 2, liquid);
  rect(g, 4, 12, 8, 2, liquidDark);
  px(g, 5, 7, 'x');
  px(g, 5, 8, 'x');
  return outline(g);
}

function shell(base: string, light: string): Grid {
  const g = blank(16, 14);
  ellipse(g, 8, 7, 7, 6, base);
  ellipse(g, 8, 7, 4.4, 3.8, light);
  ellipse(g, 8.5, 7.5, 1.8, 1.6, base);
  return outline(g);
}

function mushroomCapIcon(cap: string, capDark: string): Grid {
  const g = blank(16, 14);
  ellipse(g, 8, 8, 7, 5.4, cap);
  rect(g, 1, 8, 14, 3, cap);
  rect(g, 2, 10, 12, 2, capDark);
  ellipse(g, 5, 5, 2.4, 1.4, 'x');
  ellipse(g, 11, 6, 1.8, 1, 'x');
  return outline(g);
}

function hornIcon(): Grid {
  const g = blank(14, 16);
  for (let i = 0; i < 12; i++) {
    const w = Math.max(1, 6 - Math.floor(i / 2));
    rect(g, 4 + Math.floor(i / 3), 14 - i, w, 1, i % 4 === 0 ? 'D' : 'd');
  }
  return outline(g);
}

function ribbonIcon(): Grid {
  const g = blank(16, 12);
  ellipse(g, 4, 6, 3.4, 3, 'r');
  ellipse(g, 12, 6, 3.4, 3, 'r');
  ellipse(g, 4, 6, 1.4, 1.2, 'R');
  ellipse(g, 12, 6, 1.4, 1.2, 'R');
  rect(g, 6, 4, 4, 4, 'R');
  rect(g, 7, 8, 1, 3, 'r');
  rect(g, 9, 8, 1, 3, 'r');
  return outline(g);
}

function gooIcon(fill = 'n', shade = 'N'): Grid {
  const g = blank(16, 12);
  ellipse(g, 8, 7, 6.4, 4, fill);
  rect(g, 2, 8, 13, 3, fill);
  rect(g, 2, 10, 13, 1, shade);
  ellipse(g, 6, 5, 2.4, 1.4, 'x');
  return outline(g);
}

function branchIcon(): Grid {
  const g = blank(16, 14);
  rect(g, 7, 2, 2, 10, 'k');
  rect(g, 6, 11, 4, 2, 'K');
  px(g, 9, 4, 'k');
  px(g, 10, 3, 'k');
  px(g, 5, 6, 'k');
  px(g, 4, 5, 'k');
  return outline(g);
}

/** 메소 (동전) */
export function mesoCoin(): Grid {
  const g = blank(14, 14);
  ellipse(g, 7, 7, 6.4, 6.4, 'Y');
  ellipse(g, 7, 7, 5, 5, 'y');
  ellipse(g, 5, 5, 1.8, 1.4, 'x');
  rect(g, 6, 4, 2, 6, 'Y');
  rect(g, 4, 6, 6, 2, 'Y');
  return outline(g);
}

function swordIcon(twoHand: boolean): Grid {
  const g = blank(16, 16);
  const len = twoHand ? 12 : 10;
  for (let i = 0; i < len; i++) {
    px(g, 3 + i, 12 - i, 'X');
    px(g, 4 + i, 12 - i, twoHand ? 'y' : 'x');
  }
  rect(g, 2, 12, twoHand ? 5 : 4, 2, 'y');
  rect(g, 4, 14, 2, 2, 'k');
  return outline(g);
}

function daggerIcon(): Grid {
  const g = blank(16, 16);
  for (let i = 0; i < 7; i++) {
    px(g, 5 + i, 10 - i, 'X');
    px(g, 6 + i, 10 - i, 'x');
  }
  rect(g, 4, 10, 4, 2, 'Y');
  rect(g, 5, 12, 2, 3, 'k');
  return outline(g);
}

function wandIcon(): Grid {
  const g = blank(16, 16);
  rect(g, 7, 3, 2, 11, 'k');
  ellipse(g, 8, 3, 3, 3, 'U');
  px(g, 8, 3, 'x');
  return outline(g);
}

function bowIcon(): Grid {
  const g = blank(16, 16);
  for (let a = -70; a <= 70; a += 4) {
    const rad = (a * Math.PI) / 180;
    px(g, 5 + Math.cos(rad) * 6, 8 + Math.sin(rad) * 7, 'k');
  }
  line(g, 5, 2, 5, 14, 'K');
  px(g, 11, 8, 'y');
  return outline(g);
}

function capIcon(): Grid {
  const g = blank(16, 12);
  ellipse(g, 8, 5, 6, 3.4, 'k');
  rect(g, 2, 7, 12, 2, 'K');
  return outline(g);
}

function shirtIcon(fill: string, shade: string): Grid {
  const g = blank(16, 14);
  rect(g, 3, 3, 10, 9, fill);
  rect(g, 3, 10, 10, 2, shade);
  rect(g, 1, 4, 3, 4, fill);
  rect(g, 12, 4, 3, 4, fill);
  return outline(g);
}

function pantsIcon(fill: string, shade: string): Grid {
  const g = blank(16, 14);
  rect(g, 4, 2, 8, 4, fill);
  rect(g, 4, 6, 3, 7, fill);
  rect(g, 9, 6, 3, 7, fill);
  rect(g, 4, 11, 3, 2, shade);
  rect(g, 9, 11, 3, 2, shade);
  return outline(g);
}

function shoesIcon(): Grid {
  const g = blank(16, 10);
  rect(g, 1, 4, 6, 4, 'k');
  rect(g, 9, 4, 6, 4, 'k');
  rect(g, 1, 7, 6, 1, 'K');
  rect(g, 9, 7, 6, 1, 'K');
  return outline(g);
}

function glovesIcon(): Grid {
  const g = blank(16, 12);
  ellipse(g, 4, 6, 3, 4, 'm');
  ellipse(g, 12, 6, 3, 4, 'm');
  rect(g, 2, 8, 4, 2, 'M');
  rect(g, 10, 8, 4, 2, 'M');
  return outline(g);
}

function capeIcon(): Grid {
  const g = blank(16, 14);
  ellipse(g, 8, 8, 6, 6, 'x');
  rect(g, 5, 2, 6, 3, 'X');
  return outline(g);
}

function scrollIcon(fill: string, shade: string): Grid {
  const g = blank(16, 14);
  rect(g, 3, 2, 10, 10, 'm');
  rect(g, 3, 2, 10, 2, fill);
  rect(g, 5, 6, 6, 1, shade);
  rect(g, 5, 8, 4, 1, shade);
  return outline(g);
}

export const ITEM_ART: Record<string, () => Grid> = {
  'item-red-potion': () => potion('r', 'R'),
  'item-orange-potion': () => potion('c', 'C'),
  'item-blue-potion': () => potion('u', 'U'),
  'item-white-potion': () => potion('x', 'X'),
  'item-snail-shell': () => shell('b', 'c'),
  'item-blue-snail-shell': () => shell('U', 'u'),
  'item-mushroom-cap': () => mushroomCapIcon('c', 'C'),
  'item-horny-cap': () => hornIcon(),
  'item-pig-ribbon': () => ribbonIcon(),
  'item-slime-goo': () => gooIcon(),
  'item-blue-slime-goo': () => gooIcon('u', 'U'),
  'item-tree-branch': () => branchIcon(),
  'item-meso': () => mesoCoin(),
  'item-sword-1h': () => swordIcon(false),
  'item-sword-2h': () => swordIcon(true),
  'item-dagger': () => daggerIcon(),
  'item-wand': () => wandIcon(),
  'item-bow': () => bowIcon(),
  'item-leather-cap': () => capIcon(),
  'item-orange-top': () => shirtIcon('c', 'C'),
  'item-blue-bottom': () => pantsIcon('u', 'U'),
  'item-brown-shoes': () => shoesIcon(),
  'item-work-gloves': () => glovesIcon(),
  'item-white-cape': () => capeIcon(),
  'item-scroll-weapon': () => scrollIcon('r', 'R'),
  'item-scroll-armor': () => scrollIcon('u', 'U'),
};

/* ------------------------------- 스킬 아이콘 ------------------------------- */

function skillIconBase(tone: string, toneDark: string): Grid {
  const g = blank(22, 22);
  roundRect(g, 0, 0, 22, 22, 3, toneDark);
  roundRect(g, 1, 1, 20, 20, 2, tone);
  return g;
}

function swordGlyph(g: Grid, ox: number, oy: number): void {
  for (let i = 0; i < 12; i++) {
    px(g, ox + i, oy + 11 - i, 'X');
    px(g, ox + i + 1, oy + 11 - i, 'x');
  }
  rect(g, ox - 1, oy + 11, 4, 3, 'y');
  rect(g, ox + 1, oy + 9, 4, 3, 'Y');
}

export const SKILL_ICON_ART: Record<string, () => Grid> = {
  'icon-attack': () => {
    const g = skillIconBase('k', 'K');
    swordGlyph(g, 5, 4);
    return g;
  },
  'icon-power-strike': () => {
    const g = skillIconBase('u', 'U');
    swordGlyph(g, 5, 4);
    line(g, 16, 4, 19, 1, 'y');
    line(g, 14, 3, 16, 1, 'y');
    line(g, 18, 7, 20, 5, 'y');
    return g;
  },
  'icon-slash-blast': () => {
    const g = skillIconBase('c', 'C');
    for (let a = -60; a <= 60; a += 4) {
      const rad = (a * Math.PI) / 180;
      px(g, 6 + Math.cos(rad) * 12, 11 + Math.sin(rad) * 9, 'x');
      px(g, 6 + Math.cos(rad) * 10, 11 + Math.sin(rad) * 7.5, 'X');
    }
    swordGlyph(g, 4, 5);
    return g;
  },
  'icon-iron-body': () => {
    const g = skillIconBase('k', 'K');
    rect(g, 6, 6, 10, 10, 'X');
    rect(g, 8, 8, 6, 6, 'y');
    return g;
  },
  'icon-energy-bolt': () => {
    const g = skillIconBase('u', 'U');
    ellipse(g, 11, 11, 5, 5, 'x');
    line(g, 4, 16, 9, 12, 'U');
    return g;
  },
  'icon-magic-claw': () => {
    const g = skillIconBase('d', 'n');
    line(g, 5, 16, 16, 5, 'x');
    line(g, 7, 17, 17, 8, 'X');
    line(g, 4, 13, 15, 4, 'd');
    return g;
  },
  'icon-bless': () => {
    const g = skillIconBase('y', 'Y');
    ellipse(g, 11, 10, 6, 6, 'x');
    px(g, 11, 7, 'y');
    return g;
  },
  'icon-double-shot': () => {
    const g = skillIconBase('c', 'C');
    line(g, 4, 8, 18, 8, 'x');
    line(g, 4, 12, 18, 12, 'X');
    return g;
  },
  'icon-arrow-blow': () => {
    const g = skillIconBase('c', 'C');
    line(g, 3, 11, 18, 11, 'x');
    px(g, 18, 11, 'y');
    return g;
  },
  'icon-soul-arrow': () => {
    const g = skillIconBase('u', 'U');
    line(g, 4, 11, 17, 11, 'x');
    ellipse(g, 9, 11, 3, 3, 'U');
    return g;
  },
  'icon-lucky-seven': () => {
    const g = skillIconBase('y', 'Y');
    px(g, 8, 6, 'x');
    px(g, 14, 8, 'X');
    px(g, 11, 14, 'y');
    px(g, 6, 12, 'x');
    return g;
  },
  'icon-double-stab': () => {
    const g = skillIconBase('k', 'K');
    line(g, 5, 15, 16, 5, 'X');
    line(g, 7, 16, 17, 7, 'x');
    return g;
  },
  'icon-haste': () => {
    const g = skillIconBase('n', 'd');
    line(g, 5, 11, 17, 11, 'x');
    line(g, 12, 6, 17, 11, 'X');
    line(g, 12, 16, 17, 11, 'X');
    return g;
  },
};

export const STATUS_ICON_ART: Record<string, () => Grid> = {
  'icon-status-stun': () => {
    const g = blank(16, 16);
    roundRect(g, 1, 1, 14, 14, 3, 'y');
    ellipse(g, 8, 8, 5, 5, 'Y');
    px(g, 8, 4, 'x');
    px(g, 8, 12, 'x');
    px(g, 4, 8, 'x');
    px(g, 12, 8, 'x');
    px(g, 5, 5, 'x');
    px(g, 11, 5, 'x');
    px(g, 5, 11, 'x');
    px(g, 11, 11, 'x');
    return outline(g);
  },
  'icon-status-slow': () => {
    const g = blank(16, 16);
    roundRect(g, 1, 1, 14, 14, 3, 'u');
    ellipse(g, 8, 9, 5, 4, 'U');
    rect(g, 7, 3, 2, 5, 'x');
    px(g, 6, 6, 'X');
    px(g, 10, 6, 'X');
    return outline(g);
  },
  'icon-status-poison': () => {
    const g = blank(16, 16);
    roundRect(g, 1, 1, 14, 14, 3, 'd');
    ellipse(g, 7, 9, 4, 4, 'n');
    ellipse(g, 11, 6, 3, 3, 'N');
    px(g, 6, 8, 'x');
    px(g, 10, 5, 'x');
    return outline(g);
  },
};

/* ----------------------------- 데미지 숫자 폰트 ----------------------------- */

const DIGIT_GLYPHS: readonly string[][] = [
  ['###', '#.#', '#.#', '#.#', '###'],
  ['..#', '..#', '..#', '..#', '..#'],
  ['###', '..#', '###', '#..', '###'],
  ['###', '..#', '###', '..#', '###'],
  ['#.#', '#.#', '###', '..#', '..#'],
  ['###', '#..', '###', '..#', '###'],
  ['###', '#..', '###', '#.#', '###'],
  ['###', '..#', '..#', '..#', '..#'],
  ['###', '#.#', '###', '#.#', '###'],
  ['###', '#.#', '###', '..#', '..#'],
];

const LETTER_GLYPHS: Record<string, readonly string[]> = {
  M: ['#.#', '###', '###', '#.#', '#.#'],
  I: ['###', '.#.', '.#.', '.#.', '###'],
  S: ['###', '#..', '###', '..#', '###'],
};

export const DIGIT_SCALE = 2;
/** 확대 + 외곽선 적용 후의 글리프 한 칸 크기 */
export const DIGIT_CELL = { width: 3 * DIGIT_SCALE + 2, height: 5 * DIGIT_SCALE + 2 };

function glyphGrid(rows: readonly string[], fill: string, shade: string): Grid {
  const base = fromStrings(rows.map((r) => r.replace(/#/g, 'F').replace(/\./g, '.')));
  const scaled = scaleGrid(base, DIGIT_SCALE);
  const g = blank(DIGIT_CELL.width, DIGIT_CELL.height);
  stamp(g, scaled, 1, 1);
  // 아래쪽 절반에 음영을 준다 (원작 데미지 숫자의 그라데이션 느낌)
  for (let y = 0; y < g.length; y++) {
    for (let x = 0; x < g[y].length; x++) {
      if (g[y][x] !== 'F') continue;
      g[y][x] = y >= DIGIT_CELL.height / 2 ? shade : fill;
    }
  }
  return outline(g);
}

export function digitFont(fill: string, shade: string): Grid[] {
  return DIGIT_GLYPHS.map((rows) => glyphGrid(rows, fill, shade));
}

export function missWord(fill: string, shade: string): Grid {
  const letters = ['M', 'I', 'S', 'S'].map((c) => glyphGrid(LETTER_GLYPHS[c], fill, shade));
  const g = blank(DIGIT_CELL.width * 4, DIGIT_CELL.height);
  letters.forEach((l, i) => stamp(g, l, i * DIGIT_CELL.width, 0));
  return g;
}

/* ------------------------------- 타격 이펙트 ------------------------------- */

export const FX_PALETTE: Palette = {
  o: 'rgba(40,20,10,0.55)',
  x: '#ffffff',
  X: '#ffe9a8',
  y: '#ffd24a',
  c: '#ff9b3c',
  r: '#ff5a3c',
  u: '#9fe0ff',
  U: '#4aa8f0',
  d: '#c8a0ff',
};

/** 몬스터 적중 순간의 섬광 */
export function hitFrames(): Grid[] {
  const size = 30;
  return [0, 1, 2, 3].map((f) => {
    const g = blank(size, size);
    const c = size / 2;
    const r = 4 + f * 3.2;
    const spikes = 8;
    for (let i = 0; i < spikes; i++) {
      const a = (i / spikes) * Math.PI * 2 + f * 0.2;
      const len = r + (i % 2 === 0 ? 4 : 1.5);
      for (let t = 0; t <= len; t += 0.6) {
        const tone = t < len * 0.45 ? 'x' : t < len * 0.8 ? 'X' : 'y';
        px(g, c + Math.cos(a) * t, c + Math.sin(a) * t, tone);
      }
    }
    if (f < 3) ellipse(g, c, c, Math.max(1, 4 - f), Math.max(1, 4 - f), 'x');
    return g;
  });
}

/** 기본 공격 / 파워 스트라이크의 베기 궤적 */
export function slashFrames(strong: boolean): Grid[] {
  const w = 52;
  const h = 56;
  return [0, 1, 2].map((f) => {
    const g = blank(w, h);
    const cx = 6;
    const cy = h / 2;
    const radius = 30 + f * 6;
    const thickness = strong ? 5 : 3;
    const spread = strong ? 78 : 62;
    for (let a = -spread; a <= spread; a += 1.6) {
      const rad = (a * Math.PI) / 180;
      const fade = 1 - Math.abs(a) / (spread * 1.15);
      if (fade < f * 0.28) continue;
      for (let t = 0; t < thickness; t++) {
        const tone = t === 0 ? 'x' : t < thickness - 1 ? 'X' : strong ? 'c' : 'y';
        px(g, cx + Math.cos(rad) * (radius + t), cy + Math.sin(rad) * (radius * 0.86 + t), tone);
      }
    }
    return g;
  });
}

/** 슬래시 블래스트의 광역 휩쓸기 */
export function blastFrames(): Grid[] {
  const w = 86;
  const h = 56;
  return [0, 1, 2].map((f) => {
    const g = blank(w, h);
    const cx = w / 2;
    const cy = h / 2 + 4;
    for (let ring = 0; ring < 3; ring++) {
      const rx = 18 + ring * 11 + f * 5;
      const ry = 8 + ring * 4 + f * 2;
      for (let a = 0; a < 360; a += 2) {
        const rad = (a * Math.PI) / 180;
        const tone = ring === 0 ? 'x' : ring === 1 ? 'X' : 'c';
        if (f === 2 && ring === 0) continue;
        px(g, cx + Math.cos(rad) * rx, cy + Math.sin(rad) * ry, tone);
      }
    }
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      const len = 14 + f * 8;
      for (let t = 8; t < len; t += 0.8) {
        px(g, cx + Math.cos(a) * t, cy + Math.sin(a) * t * 0.5, t > len * 0.7 ? 'y' : 'X');
      }
    }
    return g;
  });
}

/** 레벨업 연출 */
export function levelUpFrames(): Grid[] {
  const w = 72;
  const h = 84;
  return [0, 1, 2, 3, 4, 5].map((f) => {
    const g = blank(w, h);
    const cx = w / 2;
    for (let ring = 0; ring < 3; ring++) {
      const y = h - 12 - ((ring * 26 + f * 12) % 78);
      const rx = 22 - (h - 12 - y) * 0.16;
      if (rx <= 2) continue;
      for (let a = 0; a < 360; a += 3) {
        const rad = (a * Math.PI) / 180;
        px(g, cx + Math.cos(rad) * rx, y + Math.sin(rad) * (rx * 0.3), a % 12 < 6 ? 'y' : 'X');
      }
    }
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2 + f * 0.3;
      const t = 10 + ((f * 5 + i * 3) % 26);
      px(g, cx + Math.cos(a) * t, h / 2 + Math.sin(a) * t, 'x');
      px(g, cx + Math.cos(a) * t + 1, h / 2 + Math.sin(a) * t, 'X');
    }
    return g;
  });
}

/** 뿔버섯 포자 투사체 */
export function sporeFrames(): Grid[] {
  return [0, 1].map((f) => {
    const g = blank(12, 12);
    const c = 6;
    const r = 3.2 + f * 0.4;
    ellipse(g, c, c, r, r, 'c');
    ellipse(g, c, c, r * 0.55, r * 0.55, 'y');
    px(g, c - 1, c - 1, 'x');
    return outline(g);
  });
}

export function boltFrames(): Grid[] {
  return [0, 1].map((f) => {
    const g = blank(14, 10);
    ellipse(g, 7, 5, 5 + f, 3, 'u');
    ellipse(g, 7, 5, 3, 2, 'x');
    return outline(g);
  });
}

export function arrowFrames(): Grid[] {
  return [0, 1].map((f) => {
    const g = blank(16, 8);
    line(g, 1, 4, 13, 4, f ? 'X' : 'x');
    px(g, 14, 4, 'y');
    px(g, 13, 3, 'k');
    px(g, 13, 5, 'k');
    return outline(g);
  });
}

export function starFrames(): Grid[] {
  return [0, 1].map((f) => {
    const g = blank(12, 12);
    const c = 6;
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + f * 0.4;
      line(g, c, c, c + Math.cos(a) * 5, c + Math.sin(a) * 5, i % 2 ? 'y' : 'x');
    }
    return outline(g);
  });
}
